import { faCheckCircle, faUtensils } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect } from "react";

import { priceFormatter } from "~/utils/price-formatter";
import { T } from "@repo/transifex";
import { useCurrentTillLocalisation } from "~/api/tills/hooks/use-till-current/localisation";
import { SECOND_MS } from "~/utils/constants";
import { type TicketValidationOrderDetailsResponse } from "~/hooks/queries/tickets";
import { AnimatedButton } from "~/components/animated-button";

import { TicketValidationReceiptPrinting } from "./ticket-validation-receipt-printing";

const ANIMATION_DURATION = 3 * SECOND_MS;

type TicketValidationOrderDetailsProps = {
  onClose: () => void;
  orderDetails: TicketValidationOrderDetailsResponse | null;
  shouldAutoPrint?: boolean;
  onRetry?: () => void;
};

export const TicketValidationOrderDetails = ({
  onClose,
  orderDetails,
  shouldAutoPrint,
  onRetry
}: TicketValidationOrderDetailsProps) => {
  const { locale } = useCurrentTillLocalisation();
  // Display maximum of 4 items
  const displayedItems = orderDetails ? orderDetails.products.slice(0, 4) : [];
  const remainingItemsCount = orderDetails
    ? orderDetails.products.length - displayedItems.length
    : 0;

  useEffect(() => {
    const audioPlayer = new Audio("/audio/ticket-validation-success.wav");
    audioPlayer.play();
  }, []);

  return (
    <div className="h-full bg-interface-accent-1 p-3">
      <div className="flex h-4/5 flex-col gap-8 rounded-2xl bg-white p-8">
        <div className="flex justify-between border-b border-interface-neutral-4 pb-6 pt-3">
          {orderDetails ? (
            <>
              <p className="justify-center text-right text-4xl font-normal leading-7 text-interface-neutral-6">
                <T
                  _str="{totalItems} items"
                  totalItems={orderDetails.products.length}
                />
              </p>
              <p className="justify-center text-right text-5xl font-black leading-7 text-interface-neutral-6">
                {priceFormatter({
                  value: orderDetails?.unitPriceInclVat,
                  currency: orderDetails?.currency,
                  locale
                })}
              </p>
            </>
          ) : null}
        </div>

        {!orderDetails?.products ? (
          <div className="flex h-full flex-col items-center justify-center gap-8">
            <FontAwesomeIcon
              icon={faUtensils}
              fontSize={80}
              className="text-red-500"
            />
            <p className="text-2xl font-semibold leading-loose text-red-500">
              <T _str="Order validated, but items could not be displayed" />
            </p>
          </div>
        ) : null}

        {orderDetails?.products ? (
          <div className="grid h-full grid-cols-2 gap-10">
            {displayedItems.map((item) => (
              <div key={item.name} className="flex items-center gap-4 p-3">
                {item.imageUrl ? (
                  <img
                    alt={item.name}
                    src={item.imageUrl}
                    className="h-40 w-56 rounded-3xl bg-interface-neutral-2 object-cover"
                  />
                ) : (
                  <div className="flex h-40 w-56 items-center justify-center rounded-3xl bg-interface-neutral-2">
                    <FontAwesomeIcon
                      icon={faUtensils}
                      fontSize={50}
                      className="text-interface-neutral-4"
                    />
                  </div>
                )}

                <div className="flex flex-1 flex-col gap-4">
                  <div className="flex flex-1 flex-col">
                    <p className="line-clamp-2 text-3xl font-semibold text-interface-neutral-6">
                      {item.quantity > 1 ? (
                        <span className="mr-2 text-7xl font-black">
                          {item.quantity}x
                        </span>
                      ) : null}
                      {item.name}
                    </p>
                    {item.variants.length > 0 ? (
                      <p className="line-clamp-2 text-3xl font-normal text-interface-neutral-6">
                        {item.variants
                          .map((variant) => variant.name)
                          .join(", ")}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex flex-col gap-1">
                    <p className="text-3xl font-black text-interface-neutral-6">
                      {priceFormatter({
                        value: item.unitPriceInclVat,
                        currency: orderDetails?.currency,
                        locale
                      })}
                    </p>
                    {item.depositInclVat ? (
                      <p className="flex gap-3 text-2xl font-normal text-interface-neutral-5">
                        <T _str="+ Deposit Fee" />
                        <span>
                          {priceFormatter({
                            value: item.depositInclVat,
                            currency: orderDetails.currency,
                            locale
                          })}
                        </span>
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : null}
        {remainingItemsCount > 0 ? (
          <p className="justify-center text-3xl font-black leading-7 text-interface-neutral-6">
            <T
              _str="+ {remainingItemsCount} more"
              remainingItemsCount={remainingItemsCount}
            />
          </p>
        ) : null}
      </div>

      <div className="flex h-1/5 flex-row items-center justify-between px-11">
        <div className="flex flex-row items-center justify-center gap-4">
          <FontAwesomeIcon
            icon={faCheckCircle}
            fontSize={80}
            className="text-interface-neutral-6"
          />
          <div>
            <h1 className="text-3xl font-extrabold leading-loose text-interface-neutral-6">
              <T _str="Ticket confirmed" />
            </h1>
            <p className="text-xl font-normal leading-tight text-interface-neutral-6">
              <T _str="Head over to collect your order." />
            </p>
          </div>
        </div>
        <div className="flex flex-row items-center gap-6 portrait:flex-col-reverse portrait:items-center">
          {shouldAutoPrint && onRetry ? (
            <TicketValidationReceiptPrinting variant="dark" onRetry={onRetry} />
          ) : null}
          <AnimatedButton
            className="!rounded-3xl !bg-content-lime-2/20"
            overlayClassName="bg-content-lime-2/30"
            animationDuration={ANIMATION_DURATION}
            variant="transparent"
            size="large"
            onClick={onClose}
          >
            <T _str="Close" />
          </AnimatedButton>
        </div>
      </div>
    </div>
  );
};
